import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';
import { EmptyState } from '@/components/common';

import { AccountPageState } from '../components';
import { useConsentHistory } from '../hooks';

const labels = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  'marketing-email': 'Promotions and offers',
  'product-updates-email': 'Product updates',
} as const;
const actionLabels = {
  granted: 'Granted',
  withdrawn: 'Withdrawn',
  acknowledged: 'Acknowledged',
} as const;
const sourceLabels = {
  registration: 'When your account was created',
  'account-settings': 'From account settings',
  'system-migration': 'During account setup',
} as const;

export function ConsentHistoryPage() {
  useDocumentMetadata('Consent History');
  const query = useConsentHistory();
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <AccountPageState
      loading={query.isLoading}
      error={query.isError}
      retry={() => void query.refetch()}
    >
      <div className="account-page">
        <header>
          <p className="eyebrow">Preference transparency</p>
          <h2>Consent history</h2>
          <p>
            This read-only history records acknowledgements and changes to your optional email
            choices.
          </p>
        </header>
        {items.length ? (
          <>
            <ol className="consent-history">
              {items.map((record) => (
                <li key={record.id}>
                  <span
                    className={`consent-history__action consent-history__action--${record.action}`}
                  >
                    {actionLabels[record.action]}
                  </span>
                  <div>
                    <strong>{labels[record.consentType]}</strong>
                    <p>
                      Policy {record.policyVersion} · {sourceLabels[record.source]}
                    </p>
                  </div>
                  <time dateTime={record.occurredAt}>
                    {new Intl.DateTimeFormat(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }).format(new Date(record.occurredAt))}
                  </time>
                </li>
              ))}
            </ol>
            {query.hasNextPage && (
              <button
                className="button-link button-link--secondary"
                type="button"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more history'}
              </button>
            )}
          </>
        ) : (
          <EmptyState
            icon="pi pi-history"
            title="No consent history yet"
            description="Future acknowledgements and optional communication changes will appear here."
          />
        )}
      </div>
    </AccountPageState>
  );
}
